import cn from 'classnames';
import classes from './separator.module.scss';
import { IJackpotPlayerObject, IStakeObject } from '@api/types/jackpot';
import { animated, useTransition } from 'react-spring';
import { memo, useEffect, useRef, useState } from 'react';

export interface ISeparatorStyle {
  width: number;
  color: string;
}

/* const styles = ({ width, color }: ISeparatorStyle): CSSProperties => ({
  width: `${width}%`,
  height: "100%",
  backgroundColor: `${COLORS.find((c) => c == color)}`,
}); */

export interface IColorfullSeparatorProps {
  className?: string;
  players: IJackpotPlayerObject[];
  stakes: IStakeObject[];
  total_stakes: number;
  gameUid: string;
  isCurrent: boolean;
  COLORS: string[];
}

interface ISeparatorProps {
  color: string;
  percentage: number;
  zIndex: number;
  lastOne: boolean;
  prevPercentage: number;
  isCurrent: boolean;
  containerWidth: number;
}

function Separator({ stake, COLORS }: { stake: ISeparatorProps; COLORS: string[] }): JSX.Element {
  const sepTrans = useTransition(stake, {
    config: {
      duration: 1000,
    },
    from: {
      height: '100%',
      backgroundColor: `${COLORS.find((c) => c == stake.color)}`,
      width: `${stake.prevPercentage * stake.containerWidth}px`,
      opacity: stake.isCurrent ? 0 : 1,
    },
    enter: {
      height: '100%',
      backgroundColor: `${COLORS.find((c) => c == stake.color)}`,
      width: `${stake.percentage * stake.containerWidth}px`,
      opacity: 1,
    },
    leave: {
      height: '100%',
      backgroundColor: `${COLORS.find((c) => c == stake.color)}`,
      opacity: stake.isCurrent ? 0 : 1,
    },
  });

  return sepTrans((style, item) => item && <animated.div className={classes.separator} style={style} />);
}

const ColorfulSeparator = ({ className = '', players, stakes, total_stakes, isCurrent, COLORS }: IColorfullSeparatorProps): JSX.Element => {
  const classNames = cn(classes.container, className);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.clientWidth);
    }
  }, []);

  let currentColor: number = 0;
  const playersColor: { id: string; color: string }[] = players.map((player, index) => {
    currentColor++;
    if (currentColor > COLORS.length) currentColor = 0;
    return {
      id: player.user.id,
      color: index > COLORS.length - 1 ? COLORS[currentColor] : COLORS[index],
    };
  });

  const colorList: ISeparatorProps[] = stakes.map((stake, index) => {
    const color = playersColor.find((p) => p.id === stake.userId);
    const lastPer =
      stake.stake /
      stakes.slice(0, stakes.length - 2).reduce((prev, actual) => {
        return prev + actual.stake;
      }, stakes[0].stake);
    const percentage = stake.stake !== 100 ? stake.stake / total_stakes : (stake.stake + 1) / total_stakes;

    return {
      color: color!.color,
      percentage,
      zIndex: index,
      prevPercentage: lastPer,
      lastOne: index === stakes.length - 1,
      isCurrent,
      containerWidth,
    };
  });

  return (
    <div className={classNames} ref={containerRef}>
      {colorList.map((stake, index) => (
        <Separator COLORS={COLORS} stake={stake} key={`${stake.percentage}-${index}`} />
      ))}
    </div>
  );
};

export default memo(ColorfulSeparator);
